import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Heading from "@components/layout/Heading";
import Alert from "@components/shared/Alert";
import EventTransactionPaginatedTable from "@components/table/EventTransactionPaginatedTable";
import { toastError } from "@utils/toast";
import { triggerBlobDownload } from "@utils/triggerBlobDownload";
import { useSubscriberSyncUpload } from "@context/subscriberSyncUpload/Provider";
import {
  downloadResult,
  ReclassifyResult,
} from "@services/subscriberSyncUpload";
import { useReclassifySubscriberSync } from "@hooks/mutation/useReclassifySubscriberSync";

const Summary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: level1ResourceId = "" } = useParams();
  const { state, dispatch } = useSubscriberSyncUpload();
  const summary = state.summary;
  const uploadId = state.uploadId;
  const retryableRowIds = state.retryableRowIds;
  const [downloading, setDownloading] = React.useState(false);

  // Re-checks just the rows that finalized Failed (STALE/commit errors) against current live data and
  // routes back to Review with the freshly-classified rows. Other rows — including prior declines and
  // committed rows — are untouched.
  const reclassifyMutation = useReclassifySubscriberSync({
    onSuccess: (data: ReclassifyResult) => {
      dispatch({
        type: "trial-run-loaded",
        uploadId: data.uploadId,
        rows: data.rows,
      });
    },
    onError: (err: any) =>
      toastError(
        t("subscriber_sync_upload.message.error", {
          error: err?.response?.data?.message ?? err?.message ?? "unknown",
        }),
      ),
  });

  if (!summary || !uploadId) return null;

  const handleRetry = () => {
    if (!uploadId || retryableRowIds.length === 0) return;
    reclassifyMutation.mutate({ uploadId, rowIds: retryableRowIds });
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadResult(uploadId);
      triggerBlobDownload(blob, `subscriber-sync-result-${uploadId}.xlsx`);
    } catch (err: any) {
      toastError(
        t("subscriber_sync_upload.message.error", {
          error: err?.response?.data?.message ?? err?.message ?? "unknown",
        }),
      );
    } finally {
      setDownloading(false);
    }
  };

  // Compact pill row replaces the seven-row counts table. Status-coloured chips so the operator
  // can read the headline counts at a glance and the audit-trail table below gets the rest of the
  // vertical real estate (the old table consumed ~280px even for a one-row sync).
  type ChipTone = "success" | "default" | "warning" | "error" | "info";
  const chips: Array<{ key: string; value: number; tone: ChipTone }> = [
    { key: "approved", value: summary.approved, tone: "success" },
    { key: "declined", value: summary.declined, tone: "default" },
    { key: "not_found", value: summary.notFound, tone: "warning" },
    { key: "invalid", value: summary.invalid, tone: "error" },
    { key: "in_sync", value: summary.inSync, tone: "info" },
    { key: "skipped_stale", value: summary.skippedStale, tone: "warning" },
    { key: "failed", value: summary.failed, tone: "error" },
  ];

  // Per-tone classes — kept inline so a future tone tweak is one place, not a Tailwind constant
  // file. Mirrors the existing Tag component's palette so the chip row reads as part of the
  // platform's badge family.
  const TONE_CLASS: Record<ChipTone, { dot: string; value: string }> = {
    success: { dot: "bg-green-500", value: "text-green-700" },
    default: { dot: "bg-gray-400", value: "text-gray-700" },
    warning: { dot: "bg-amber-500", value: "text-amber-700" },
    error: { dot: "bg-red-500", value: "text-red-700" },
    info: { dot: "bg-blue-500", value: "text-blue-700" },
  };

  return (
    <>
      <div className="flex flex-col grow gap-6 p-6">
        <Heading light>{t("subscriber_sync_upload.summary.title")}</Heading>

        <Alert type="success">
          {t("subscriber_sync_upload.info.complete")}
        </Alert>

        <div
          role="list"
          aria-label={t("subscriber_sync_upload.summary.title") as string}
          className="flex flex-wrap gap-2"
        >
          {chips.map(({ key, value, tone }) => (
            <div
              key={key}
              role="listitem"
              data-testid={`summary-chip-${key}`}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm"
            >
              <span
                aria-hidden="true"
                className={`inline-block h-2 w-2 rounded-full ${TONE_CLASS[tone].dot}`}
              />
              <span className="text-gray-700">
                {t(`subscriber_sync_upload.summary.${key}`)}
              </span>
              <span
                className={`font-semibold tabular-nums ${TONE_CLASS[tone].value}`}
              >
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Mirrors bulk meter upload's WorkflowComplete: after the ledger run, surface the
            audit-trail events for this run's workflow instance. Filtering by workflowInstanceId
            (not the upload id) catches the bulk-upload root event AND every per-row child, so
            the table is the full ledger tree. Falls back to an empty filter when the id isn't
            available (e.g. on an older draft resumed and finalized in a session where the
            response didn't carry workflowInstance — rare but defensive). */}
        {state.workflowInstanceId ? (
          <div className="flex flex-col grow h-full">
            <EventTransactionPaginatedTable
              references={[state.workflowInstanceId]}
              stickyHeader={true}
            />
          </div>
        ) : null}
      </div>

      <footer className="flex gap-4 border-t border-gray-300 p-6">
        <button
          type="button"
          className="btn-primary"
          disabled={downloading}
          onClick={handleDownload}
        >
          {t("subscriber_sync_upload.summary.download_result")}
        </button>
        {retryableRowIds.length > 0 ? (
          <button
            type="button"
            className="btn-outline-primary"
            disabled={reclassifyMutation.isLoading}
            onClick={handleRetry}
          >
            {t("subscriber_sync_upload.summary.retry_skipped", {
              count: retryableRowIds.length,
            })}
          </button>
        ) : null}
        <button
          type="button"
          className="btn-outline-primary"
          onClick={() =>
            navigate(
              level1ResourceId
                ? `/polestar/level1wrs/${level1ResourceId}`
                : "/polestar/subscribers/sync_uploads",
            )
          }
        >
          {t("common.close")}
        </button>
      </footer>
    </>
  );
};

export default Summary;
